import axios, { AxiosResponse } from 'axios';
import Service from '../Service';
import { createCoreServiceUrl } from '../../commons/services';
import { Invoice, InvoiceItem, InvoiceItemFilters, Page } from '../../types/type';
import moment from 'moment';

class InvoiceService extends Service {
  public saveInvoice = (invoice: Invoice, userId = ''): Promise<Invoice> =>
    axios
      .post(createCoreServiceUrl('v1/invoice'), {
        invoice: { ...invoice },
        sales: invoice.sales,
        ...(userId ? { userId } : {}),
      })
      .then((response: AxiosResponse<Invoice>): Invoice => response.data);
  public saveInvoiceMultiple = (
    invoice: Invoice,
    userId = '',
  ): Promise<Invoice> =>
    axios
      .post(createCoreServiceUrl('invoice/multiple'), {
        invoice: { ...invoice },
        sales: invoice.sales,
        ...(userId ? { userId } : {}),
      })
      .then((response: AxiosResponse<Invoice>): Invoice => response.data);
  public resendInvoce = (id: string): Promise<Invoice> =>
    axios
      .post(createCoreServiceUrl(`sale/invoice/${id}`))
      .then((response: AxiosResponse<Invoice>): Invoice => response.data);
  public resendInvoice = (id: string, sendTo: string): Promise<Invoice> =>
    axios
      .post(createCoreServiceUrl(`sale/invoice/resend/${id}`), null, {
        params: { sendTo },
      })
      .then((response: AxiosResponse<Invoice>): Invoice => response.data);
  public cancelInvoice = (id: string, reason: string): Promise<string> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await axios.get(
          createCoreServiceUrl(`sale/cancelInvoiceSale/${id}?reason=${reason}`),
        );
        if (
          response.data.message &&
          (response.data.message === 'Cancelado sin aceptación' ||
            response.data.message === 'Cancelado con aceptación' ||
            response.data.message === 'En proceso')
        ) {
          resolve(response.data.description);
        }
        else {
          reject(new Error(response.data.message || 'Cancelación fallida'));
        }
      } catch (error) {
        reject(error);
      }
      return 'ok';
    });
  public cancelInvoiceByUuidSat = (
    uuidSat: string,
    reason: string,
  ): Promise<string> =>
    new Promise(async (resolve, reject) => {
      try {
        const response = await axios.get(
          createCoreServiceUrl(
            `sale/cancelInvoiceByUuidSat/${uuidSat}?reason=${reason}`,
          ),
        );
        if (
          response.data.message &&
          (response.data.message === 'Cancelado sin aceptación' ||
            response.data.message === 'Cancelado con aceptación' ||
            response.data.message === 'En proceso')
        ) {
          resolve(response.data.description);
        } else {
          reject(new Error(response.data.description || 'Cancelación fallida'));
        }
      } catch (error) {
        reject(error);
      }
      return 'ok';
    });
  public saveInvoiceAndCancel = (
    invoice: Invoice,
    userId = '',
  ): Promise<Invoice> =>
    axios
      .post(
        createCoreServiceUrl('v1/invoiceAndCancel'),
        {
          invoice: { ...invoice },
          sales: invoice.sales,
          ...(userId ? { userId } : {}),
        },
        {
          params: { multiple: false },
        },
      )
      .then((response: AxiosResponse<Invoice>): Invoice => response.data);
  public saveInvoiceAndCancelMultiple = (
    invoice: Invoice,
    userId = '',
  ): Promise<Invoice> =>
    axios
      .post(
        createCoreServiceUrl('v1/invoiceAndCancel'),
        {
          invoice: { ...invoice },
          sales: invoice.sales,
          ...(userId ? { userId } : {}),
        },
        {
          params: { multiple: true },
        },
      )
      .then((response: AxiosResponse<Invoice>): Invoice => response.data);
  public getPaginatedMultipleInvoicesByFilters = ({
    initDate,
    endDate,
    serviceNumberIds,
    equipmentIds,
    type,
    status,
    paymentMethod,
    rfc,
    uuidSat,
    statusSat,
    cancelableSat,
    cancellationStatusSat,
    distCenterId,
    page,
    size,
  }: InvoiceItemFilters): Promise<Page<InvoiceItem>> => {
    const rawParams: Record<string, any> = {
      initDate: moment(initDate).format('YYYY-MM-DD HH:mm:ss'),
      endDate: moment(endDate).format('YYYY-MM-DD HH:mm:ss'),
      serviceNumberIds: !!serviceNumberIds && serviceNumberIds?.length
        ? serviceNumberIds.join(',')
        : undefined,
      equipmentIds: !!equipmentIds && equipmentIds.length ? equipmentIds.join(',') : undefined,
      type,
      status,
      paymentMethod,
      rfc,
      uuidSat,
      statusSat,
      cancelableSat,
      cancellationStatusSat,
      distCenterId,
      page,
      size,
    };

    const filteredParams = Object.fromEntries(
      Object.entries(rawParams).filter(
        ([_, value]) => value !== 'ALL' && value !== '' && value != null,
      ),
    );

    return axios
      .get(createCoreServiceUrl(`v1/invoices/paginated`), {
        params: filteredParams,
        headers: {
          'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      })
      .then(
        (response: AxiosResponse<Page<InvoiceItem>>): Page<InvoiceItem> =>
          response.data,
      );
  };

  public createExcelInvoicesByFilters = ({
    initDate,
    endDate,
    serviceNumberIds,
    equipmentIds,
    type,
    status,
    paymentMethod,
    rfc,
    uuidSat,
    statusSat,
    cancelableSat,
    cancellationStatusSat,
    distCenterId,
    page,
    size,
  }: InvoiceItemFilters,
    emailToSend: string,
  ): Promise<void> => {
    const rawParams: Record<string, any> = {
      initDate: moment(initDate).format('YYYY-MM-DD HH:mm:ss'),
      endDate: moment(endDate).format('YYYY-MM-DD HH:mm:ss'),
      serviceNumberIds: !!serviceNumberIds && serviceNumberIds.length
        ? serviceNumberIds.join(',')
        : undefined,
      equipmentIds: !!equipmentIds && equipmentIds.length ? equipmentIds.join(',') : undefined,
      type,
      status,
      paymentMethod,
      rfc,
      uuidSat,
      statusSat,
      cancelableSat,
      cancellationStatusSat,
      distCenterId,
      page,
      size,
      emailToSend,
    };

    const filteredParams = Object.fromEntries(
      Object.entries(rawParams).filter(
        ([_, value]) => value !== 'ALL' && value !== '' && value != null,
      ),
    );

    return (
      axios
        .get(createCoreServiceUrl(`v1/invoices/createExcel`), {
          params: filteredParams,
          headers: {
            'X-Timezone': Intl.DateTimeFormat().resolvedOptions().timeZone,
          },
        })
        // eslint-disable-next-line @typescript-eslint/no-empty-function
        .then((): void => {})
    );
  };

  public getInvoiceById = (id: string): Promise<InvoiceItem> =>
    axios
      .get(createCoreServiceUrl(`v1/invoices/multiple/${id}`))
      .then(
        (response: AxiosResponse<InvoiceItem>): InvoiceItem => response.data,
      );
}

export default InvoiceService;
